<script lang="ts">
    import type {DiskUsage, PublicStatsData} from '../../types';
    import {getColorFromString} from '../../utils';
    import {pipe} from 'core/utils';
    import {byteFormatter} from 'shared/utils/pipes';
    import {exists} from 'shared/utils/custom-utils';
    import {getLocalization} from 'core/svelte-context/context';
    import KpTitledSection from 'shared/layouts/KpTitledSection.svelte';

    export let statsData: PublicStatsData;
    const localize = getLocalization();
    $: diskUsages = statsData.stats.result.discSpaceConsumptionStatProvider.counts;
    $: isEmpty = diskUsages.length === 0;

    let hoveredOverUsage: DiskUsage | null = null;

    function calculateUsageWidth(fileExtension: string): number {
        const totalSize = getTotalSize();
        const diskUsageSize = getFileExtensionSize(fileExtension);
        return (diskUsageSize / totalSize) * 100;
    }

    function getFileExtensionSize(fileExtension: string): number {
        const diskUsage = diskUsages.find((usage) => usage.name === fileExtension);
        return diskUsage?.value ?? 0;
    }

    function sortedDiskUsages(): DiskUsage[] {
        return diskUsages.toSorted((a, b) => b.value - a.value);
    }

    function getTotalSize(): number {
        let totalSize = 0;
        diskUsages.forEach((usage) => totalSize += usage.value);
        return totalSize;
    }

    const handleLegendMouseEnter = (diskUsage: DiskUsage) => {
        hoveredOverUsage = diskUsage;
    }

    const handleLegendsMouseLeave = () => {
        hoveredOverUsage = null;
    }
</script>

{#if !isEmpty}
    <KpTitledSection additionalClasses="stats-disk-usage-info" headingType="h3">
        <div slot="heading" class="heading-container">
            <h3>
                {localize(/* @kp-localization file.DiskUsage */ 'file.DiskUsage')}
            </h3>

            <span class="text-muted">
                {localize(/* @kp-localization commons.Total */ 'commons.Total')}:
                {pipe(getTotalSize(), byteFormatter(2))}
            </span>
        </div>

        <svelte:fragment>
            <div class="disk-usage-graph" class:no-highlighting={!exists(hoveredOverUsage)}>
                {#each sortedDiskUsages() as diskUsage}
                    <div class="disk-usage"
                         class:not-highlighted={exists(hoveredOverUsage) && diskUsage.name !== hoveredOverUsage.name}
                         class:highlighted={exists(hoveredOverUsage) && diskUsage.name === hoveredOverUsage.name}
                         style:--width="{calculateUsageWidth(diskUsage.name)}%"
                         style:--bg-color="{getColorFromString(diskUsage.name)}">
                    </div>
                {/each}
            </div>

            <div class="legends-container"
                 role="application"
                 on:mouseleave={handleLegendsMouseLeave}>

                {#each sortedDiskUsages() as diskUsage}
                    <div class="file-extension-legend"
                         role="application"
                         on:mouseenter={() => handleLegendMouseEnter(diskUsage)}
                         style:--file-extension-color="{getColorFromString(diskUsage.name)}">

                        <div class="file-extension-color"></div>

                        <div class="info-container">
                            <span class="file-extension">{diskUsage.name ? diskUsage.name : '-'}</span>

                            <small class="total-size text-muted">
                                {pipe(getFileExtensionSize(diskUsage.name), byteFormatter(2))}
                            </small>
                        </div>
                    </div>
                {/each}
            </div>
        </svelte:fragment>
    </KpTitledSection>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .heading-container {
        display: flex;
        align-items: center;
        justify-content: space-between;

        h3 {
            margin: 0;
        }
    }

    .disk-usage-graph {
        width: 100%;
        display: flex;
        border-radius: @border-radius-default;

        &.no-highlighting {
            overflow: hidden;
        }

        .disk-usage {
            height: 56px;
            display: flex;
            flex-direction: column;
            gap: @spacing-s;
            align-items: center;
            justify-content: center;
            background-color: var(--bg-color);
            width: var(--width);
            outline-offset: 1px;
            outline: 2px solid transparent;
            transition: opacity 0.3s ease-in-out, outline-color 0.3s ease-in-out;

            &:first-child {
                border-top-left-radius: @border-radius-default;
                border-bottom-left-radius: @border-radius-default;
            }

            &:last-child {
                border-top-right-radius: @border-radius-default;
                border-bottom-right-radius: @border-radius-default;
            }

            &.not-highlighted {
                opacity: 0.33;
            }

            &.highlighted {
                outline-color: var(--accent-blue-new);
            }
        }
    }

    .legends-container {
        display: flex;
        gap: @spacing-xl;
        align-items: center;
        flex-wrap: wrap;

        .file-extension-legend {
            display: flex;
            gap: @spacing-m;
            padding: 0 @spacing-s;
            min-height: 36px;
            cursor: pointer;
            align-items: center;
            border-radius: @border-radius-default;
            outline: 2px solid transparent;
            transition: outline-color 0.3s ease-in-out;

            &:hover {
                outline-color: var(--accent-blue-new);
            }

            .file-extension-color {
                width: 28px;
                height: 28px;
                border-radius: @border-radius-default;
                background-color: var(--file-extension-color);
            }

            .info-container {
                display: flex;
                flex-direction: column;
            }
        }
    }
</style>